import { CancelablePromise, OpenAPI, request as __request } from '/src/api';
import { Statement, Transaction } from '/src/lib/models';
import {
  DataModelPluralType,
  type TListStatementTransactionsRequest,
  type TListStatementTransactionsResponse,
} from '/src/lib/types';

export class StatementTransactionService {
  private static _interceptor = (transactions: Transaction[]) => {
    const statements: Statement[] = [];
    // Prepare totals (for entire transaction history)
    const totals = {
      feesCharged: 0,
      feeTotal: 0,
      feesPaid: 0,
      interestCharged: 0,
      interestPaid: 0,
      principalPaid: 0,
      currentPrincipal: 0,
      advanceAmount: 0,
      adjustmentAmount: 0,
      paymentAmount: 0,
    };
    let allAdjustments = true;

    // Pre-process the data
    if (transactions.length) {
      let transaction;

      for (const txn in transactions) {
        transaction = transactions[txn];

        // Push all statement transactions to the Statements array
        if (transaction.__t === DataModelPluralType.Statement) statements.push(transaction as Statement);

        // Skip the transaction if it's an adjustment
        if (transaction.__t !== DataModelPluralType.Adjustment) {
          // Set the 'list is all adjustments' flag to false
          allAdjustments = false;

          // Define some item-specific totals
          if (transaction.__t === DataModelPluralType.Advance) {
            transaction.advanceAmount = transaction.amount;
            totals.advanceAmount += transaction.amount;
          } else if (transaction.__t === DataModelPluralType.Payment) {
            transaction.paymentAmount = transaction.amount;
            totals.paymentAmount += transaction.amount;
          }

          // Total fees
          transaction.feeTotal = transaction.fees + transaction.lateFee;
          totals.feeTotal += transaction.feeTotal;

          // Fees charged
          totals.feesCharged += transaction.feeTotal;

          // Fees due
          transaction.feesDue = transaction.feeBalance + transaction.feeTotal;

          // Fees paid
          if (transaction.appliedToFees) {
            transaction.feesDue -= transaction.appliedToFees;
            totals.feesPaid += transaction.appliedToFees;
          }

          // Interest due
          transaction.interestDue = transaction.interestBalance + transaction.interestCharge;
          totals.interestCharged += transaction.interestCharge;

          // Interest paid
          if (transaction.appliedToInterest) {
            transaction.interestDue -= transaction.appliedToInterest;
            totals.interestPaid += transaction.appliedToInterest;
          }

          // Principal balance
          if (transaction.__t === DataModelPluralType.Advance) {
            transaction.newPrincipalBalance = transaction.principalBalance + transaction.amount;
            totals.currentPrincipal = transaction.newPrincipalBalance;
          } else if (transaction.__t === DataModelPluralType.Payment) {
            transaction.newPrincipalBalance = transaction.principalBalance - transaction.appliedToPrincipal;
            totals.currentPrincipal = transaction.newPrincipalBalance;
            totals.principalPaid += transaction.appliedToPrincipal;
          } else if (transaction.__t === DataModelPluralType.Adjustment) {
            transaction.newPrincipalBalance = transaction.principalBalance;
            totals.currentPrincipal = transaction.newPrincipalBalance;
          }
        }
      }

      if (transactions && transactions[0]) {
        if (transactions[0].newPrincipalBalance) totals.currentPrincipal = transactions[0].newPrincipalBalance;
        if (transactions[0].assetValue) totals.currentAssets = transactions[0].assetValue;
        if (transactions[0].currentAvailableCredit)
          totals.currentAvailableCredit = transactions[0].currentAvailableCredit;
      }
    }

    // Return the data
    return { transactions, totals, allAdjustments };
  };

  /**
   * Get Loan Transactions
   */
  public static async query(
    req: TListStatementTransactionsRequest
  ): CancelablePromise<TListStatementTransactionsResponse> {
    return this._interceptor(
      await __request(OpenAPI, {
        method: 'GET',
        url: '/transactions',
        query: req,
      })
    );
  }
}
